import React, { useContext, useState } from 'react'
import { Grid, Button } from '@mui/material'
import { Link } from 'react-router-dom'
import { AppContext } from '../context/AppContext'
import ExternSurface from '../components/ExternSurface'
import ConfirmPasswordForm from './ConfirmPasswordForm'
import UpdatePasswordForm from './UpdatePasswordForm'
import { CognitoUser } from 'amazon-cognito-identity-js'
import MFACodeForm from './MFACodeForm'
import {
    AnyInput,
    FeedbackContext,
    useValidatedForm,
} from '@focaltec-suite/commons'
import { authenticate } from '@focaltec-suite/api'
import { translate, cognitoErrorTranslator } from '@focaltec-suite/core'

interface SignInRequest {
    email: string
    password: string
}

type Mode = 'sign_in' | 'update_password' | 'confirm_password' | 'required_2fa'

export default function SignInForm() {
    const context = useContext(AppContext)
    const feedbackContext = useContext(FeedbackContext)

    const [mode, setMode] = useState<Mode>('sign_in')
    const { register, bind, isValid } = useValidatedForm<SignInRequest>()
    const [cognitoUser, setCognitoUser] = useState<CognitoUser>()
    const [email, setEmail] = useState('')

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (!isValid()) {
            return
        }

        feedbackContext.showBackdrop()

        const request = bind()
        authenticate(request.email, request.password)
            .then((response) => {
                switch (response.name) {
                    case 'success':
                        context.onSignedIn()
                        break
                    case 'update_password':
                    case 'confirm_password':
                    case 'required_2fa':
                        setEmail(request.email)
                        setCognitoUser(response.cognitoUser)
                        setMode(response.name)
                        feedbackContext.closeBackdrop()
                        break
                    default:
                        feedbackContext.closeBackdrop()
                        break
                }
            })
            .catch((error) => {
                console.error(error)
                feedbackContext.closeBackdrop()
                cognitoErrorTranslator(error, feedbackContext.showError)
            })
    }

    const reset = () => {
        setMode('sign_in')
    }

    if (mode === 'sign_in') {
        return (
            <ExternSurface title={translate('signin.title') as string}>
                <form autoComplete="off" noValidate onSubmit={onSubmit}>
                    <Grid container justifyContent="center" alignItems="center">
                        <Grid item xs={12}>
                            <AnyInput
                                type="email"
                                name="email"
                                label={translate('email') as string}
                                required
                                hiddenLabel
                                register={register}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <AnyInput
                                type="password"
                                name="password"
                                label={translate('password') as string}
                                required
                                hiddenLabel
                                register={register}
                            />
                        </Grid>
                        <Grid item xs={12} sx={{ my: 1 }}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                fullWidth
                                size="large"
                            >
                                {translate('signin.button')}
                            </Button>
                        </Grid>
                        <Grid item xs="auto">
                            <Button
                                variant="text"
                                color="primary"
                                size="large"
                                component={Link}
                                to={context.fixPath('/recover-password')}
                            >
                                {translate('signin.recover')}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </ExternSurface>
        )
    }

    if (mode === 'update_password') {
        return (
            <UpdatePasswordForm
                email={email}
                cognitoUser={cognitoUser}
                onSuccess={() => context.onSignedIn()}
                onCancel={() => setMode('sign_in')}
            />
        )
    }

    if (mode === 'required_2fa') {
        return (
            <MFACodeForm
                cognitoUser={cognitoUser}
                onSuccess={() => context.onSignedIn()}
                onCancel={() => setMode('sign_in')}
            />
        )
    }

    return (
        <ConfirmPasswordForm
            mode="new"
            cognitoUser={cognitoUser}
            onSuccess={reset}
            onCancel={() => setMode('sign_in')}
        />
    )
}
